import { Helmet } from "react-helmet-async";
import { useLocation, useMatches, useParams } from "react-router";
import { useTranslation } from "react-i18next";

const EXACT_PATH_TITLES: Record<string, string> = {
	// Root / Home
	"/": "Home",
	"/dashboard": "Home",
	"/dashboard/home": "Home",

	// Public / Authentication
	"/login": "Sign In",
	"/new-login": "Sign In",
	"/register-client": "Register",
	"/sign-up-user": "Sign Up",
	"/forgot-password": "Forgot Password",
	"/reset-password": "Reset Password",
	"/verify-user-email": "Verify Email",

	// Errors
	"/403": "403 Forbidden",
	"/404": "404 Page Not Found",
	"/500": "500 Internal Server Error",

	// Dashboard Playgrounds & Variants
	"/dashboard/dashboard-playgrounds": "Smart Dashboard Agent",
	"/dashboard/smart-dashboard-agent": "Smart Dashboard Agent",
	"/dashboard/dashboardPlayground": "Dashboard Playground",
	"/dashboard/legacy-dashboard-playgrounds": "Dashboard Playground",
	"/dashboard/new-dashboard": "New Dashboard",
	"/dashboard/new-dashboard-powerbi": "Power BI Dashboard",

	// User, Account, Billing & Payment
	"/account": "Account",
	"/profile": "Profile",
	"/dashboard/upgrade-plan": "Billing",
	"/dashboard/redirecting-to-payment-partner": "Checkout",
	"/dashboard/waiting-for-payment-confirmation": "Waiting for Payment",
	"/dashboard/payment-error": "Payment Error",
	"/dashboard/transfer-dashboards": "Transfer Dashboards",
	"/dashboard/transfer-dashboards-success": "Transfer Success",
	"/payment/success": "Payment Success",
	"/payment/cancelled": "Payment Cancelled",

	// AI Agents
	"/agents": "AI Agents",
	"/agents/upload-csv": "Data Preparation Agent",
	"/agents/data-analysis-agent": "Data Analysis Agent",
	"/agents/new-data-analysis-agent": "Data Analysis Agent",
	"/agents/new-data-analysis-agent-powerbi": "Power BI Data Analysis Agent",
	"/agents/merge-files": "Merge Datasets",
	"/agents/data-merging": "Merge Datasets",
	"/agents/smart-dashboard-agent": "Smart Dashboard Agent",
	"/agents/data-cleaning": "Data Cleaning",
	"/agents/enhanced-data-cleaning": "Data Cleaning",
	"/agents/legacy-data-cleaning": "Legacy Data Cleaning",

	// Configurations
	"/configuration": "Configuration",
	"/configuration/widjet-config": "Widget Configuration",
	"/configuration/dashboard-config": "Dashboard Configuration",
	"/configuration/datasource-config": "Data Source Configuration",
	"/configuration/new-datasource-config": "Data Source Configuration",
	"/configuration/external-link-config": "External Link Configuration",
	"/configuration/dashboard-management": "Dashboard Management",

	// Management
	"/management": "Management",
	"/management/user": "Users Management",
	"/management/feature": "Feature Management",

	// Others & Components
	"/calendar": "Calendar",
	"/kanban": "Kanban",
	"/components/icon": "Icon",
	"/components/animate": "Animate",
	"/components/scroll": "Scroll",
	"/components/markdown": "Markdown",
	"/components/editor": "Editor",
	"/components/i18n": "Multi Language",
	"/components/upload": "Upload",
	"/components/chart": "Chart",
	"/components/toast": "Toast",
	"/functions/clipboard": "Clipboard",
	"/frame/external_link": "External Link",
	"/frame/iframe": "Iframe",
	"/blank": "Blank",
};

function formatSegment(segment: string): string {
	return segment
		.split(/[-_]+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

export default function PageTitleUpdater() {
	const location = useLocation();
	const matches = useMatches();
	const params = useParams();
	const { t } = useTranslation();

	const roleId = localStorage.getItem("roleId");

	const getPageTitle = (): string => {
		const pathname = location.pathname.replace(/\/+$/, "") || "/";

		// 1. Check exact match mapping
		if (EXACT_PATH_TITLES[pathname]) {
			return EXACT_PATH_TITLES[pathname];
		}

		// 2. Dynamic role management title
		if (pathname === "/management/role") {
			return roleId === "1" ? "Clients Management" : "Departments Management";
		}

		// 3. Dynamic dashboard route: /dashboard/:dashboard_id/:dashboard_name
		if (pathname.startsWith("/dashboard/") && params.dashboard_name) {
			try {
				const decoded = decodeURIComponent(params.dashboard_name);
				return formatSegment(decoded);
			} catch {
				return formatSegment(params.dashboard_name);
			}
		}

		// 4. Check route matches metadata if available
		const lastMatch = matches.at(-1);
		const routeData = lastMatch?.data as { meta?: { label?: string } } | undefined;
		const routeHandle = lastMatch?.handle as { label?: string; title?: string } | undefined;
		const rawLabel = routeData?.meta?.label || routeHandle?.label || routeHandle?.title;

		if (rawLabel) {
			// If label is error index but current path is not an error page, don't show Error
			if (rawLabel.includes("error") && !pathname.includes("error") && !["/403", "/404", "/500"].includes(pathname)) {
				return "Home";
			}
			const translated = t(rawLabel);
			if (translated && translated !== rawLabel) {
				return translated;
			}
			return rawLabel;
		}

		// 5. Fallback from pathname segments
		const segments = pathname.split("/").filter(Boolean);
		if (segments.length > 0) {
			const lastSegment = segments[segments.length - 1];
			return formatSegment(lastSegment);
		}

		return "Home";
	};

	const rawTitle = getPageTitle().trim();
	const fullTitle = rawTitle.startsWith("VantageAI |")
		? rawTitle
		: rawTitle.length > 0
			? `VantageAI | ${rawTitle}`
			: "VantageAI";

	// useEffect(() => {
	// 	document.title = fullTitle;

	// 	// Push page_view event to Google Tag Manager (GTM)
	// 	if (typeof window !== "undefined") {
	// 		(window as any).dataLayer = (window as any).dataLayer || [];
	// 		(window as any).dataLayer.push({
	// 			event: "page_view",
	// 			page_path: location.pathname + location.search + location.hash,
	// 			page_title: fullTitle,
	// 			page_location: window.location.href,
	// 		});
	// 	}
	// }, [fullTitle, location.pathname, location.search, location.hash]);

	return (
		<Helmet>
			<title>{fullTitle}</title>
		</Helmet>
	);
}
